import { isEqual } from 'lodash';
import { useMemo, useState } from 'react';
import {
  Option
} from '../controls/optionPickerLib';
import { useTranslation } from '@lib/useTypedTranslation';

export type TimePeriod = number;

export interface ITimePeriodPickerProps {
  timePeriod: Option<TimePeriod>,
  timePeriodOptions: Option<TimePeriod>[],
  handleTimePeriodSelect: (selected?: Option<TimePeriod>) => void
}

export const useTimePeriodPicker = (params: { timePeriods: TimePeriod[], enableAllTimeOption?: boolean }): ITimePeriodPickerProps => {
  const { t } = useTranslation(['timeState']);

  const timePeriodOptions: ITimePeriodPickerProps['timePeriodOptions'] = useMemo(
    () => {
      const options: ITimePeriodPickerProps['timePeriodOptions'] = [];
      if (params.enableAllTimeOption) {
        options.push({ name: t('ALL_TIME', { ns: 'timeState' }), value: null });
      }

      options.push(...params.timePeriods.map((value) => ({
        name: value <= 2 ? t('LAST_N_HOURS', { count: value * 24, ns: 'timeState' }) : t('LAST_N_DAYS', { count: value, ns: 'timeState' }),
        value
      })));

      return options;
    },
    [t, params.timePeriods, params.enableAllTimeOption]
  );

  const initialState = timePeriodOptions[0];
  const [timePeriod, setTimePeriod] = useState<ITimePeriodPickerProps['timePeriod']>(initialState);

  const handleTimePeriodSelect = (selected: ITimePeriodPickerProps['timePeriod']) => {
    if (isEqual(selected, timePeriod)) { return; }
    setTimePeriod(selected);
  };

  return {
    timePeriod,
    timePeriodOptions,
    handleTimePeriodSelect
  };
};
